import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const StudioSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/studio-dashboard',
      icon: 'LayoutDashboard',
      current: location.pathname === '/studio-dashboard'
    },
    {
      name: 'Games',
      href: '/studio-games',
      icon: 'Gamepad2',
      current: location.pathname === '/studio-games'
    },
    {
      name: 'Analytics',
      href: '/studio-analytics',
      icon: 'BarChart3',
      current: location.pathname === '/studio-analytics'
    },
    {
      name: 'Revenue',
      href: '/studio-revenue',
      icon: 'DollarSign',
      current: location.pathname === '/studio-revenue'
    },
    {
      name: 'SDK Integration',
      href: '/studio-sdk',
      icon: 'Code',
      current: location.pathname === '/studio-sdk'
    },
    {
      name: 'API Keys',
      href: '/studio-api-keys',
      icon: 'Key',
      current: location.pathname === '/studio-api-keys'
    }
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-card border-r border-border px-6 pb-4">
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Building" size={20} className="text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Studio Portal</h1>
              <p className="text-xs text-muted-foreground">Solis Games</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <button
                      onClick={() => navigate(item.href)}
                      className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full text-left transition-colors ${
                        item.current
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon
                        name={item.icon}
                        size={20}
                        className={`shrink-0 ${
                          item.current ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                        }`}
                      />
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </li>

            {/* Quick Links */}
            <li>
              <div className="text-xs font-semibold leading-6 text-muted-foreground">
                Quick Links
              </div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                <li>
                  <button
                    onClick={() => navigate('/')}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full text-left"
                  >
                    <Icon name="Home" size={20} className="shrink-0 text-muted-foreground group-hover:text-foreground" />
                    Main Site
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.open('https://docs.solisgames.com', '_blank')}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full text-left"
                  >
                    <Icon name="BookOpen" size={20} className="shrink-0 text-muted-foreground group-hover:text-foreground" />
                    Documentation
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/contact')}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full text-left"
                  >
                    <Icon name="MessageCircle" size={20} className="shrink-0 text-muted-foreground group-hover:text-foreground" />
                    Support
                  </button>
                </li>
              </ul>
            </li>

            {/* User Profile */}
            <li className="mt-auto">
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-x-4 px-2 py-3 text-sm font-semibold leading-6 text-foreground">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Studio Admin'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                
                <div className="mt-2 space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Settings"
                    className="w-full justify-start"
                    onClick={() => navigate('/user-profile-settings')}
                  >
                    Settings
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="LogOut"
                    className="w-full justify-start"
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default StudioSidebar;
